import { Head, usePage } from '@inertiajs/react';

const NO_APP_SUFFIX_MARKER = '__NO_APP_SUFFIX__';

const isFilled = (value) => typeof value === 'string' && value.trim() !== '';

const composeWithCompany = (base, companyName) => {
    const trimmedBase = isFilled(base) ? base.trim() : '';
    const trimmedCompany = isFilled(companyName) ? companyName.trim() : '';

    if (!trimmedBase) {
        return trimmedCompany;
    }

    if (!trimmedCompany || trimmedBase.toLowerCase() === trimmedCompany.toLowerCase()) {
        return trimmedBase;
    }

    return `${trimmedBase} - ${trimmedCompany}`;
};

/**
 * Renders SEO meta tags for the current page.
 *
 * Pages MUST pass only the `meta` object produced by `MetaResource` on the backend.
 * The resource already applies the full fallback chain
 * (entity.seo_* -> entity.title -> settings -> APP_NAME) and includes the company
 * name suffix in the title, the description and a default cover image.
 *
 * If `meta` is missing or empty, this component still renders a sensible default
 * built from the shared `siteSettings` so every page has at least a title and
 * og:site_name.
 *
 * The trailing `__NO_APP_SUFFIX__` marker prevents the global title resolver in
 * `resources/js/app.jsx` from appending APP_NAME a second time.
 */
export default function MetaHead({ meta = null, children = null }) {
    const { props, url } = usePage();
    const siteSettings = props?.siteSettings || {};
    const companyName = siteSettings?.companyName || '';
    const defaultImage = siteSettings?.coverImage || null;

    const metaObject = meta && typeof meta === 'object' && !Array.isArray(meta)
        ? meta
        : {};

    const resolvedTitle = isFilled(metaObject.title)
        ? metaObject.title
        : composeWithCompany('', companyName);

    const resolvedDescription = isFilled(metaObject.description)
        ? metaObject.description
        : null;

    const resolvedImage = isFilled(metaObject.image)
        ? metaObject.image
        : defaultImage;

    const canonicalUrl = typeof window !== 'undefined'
        ? window.location.href
        : `${props?.app_url || ''}${url || ''}`;

    const ogType = 'website';
    const twitterCard = resolvedImage ? 'summary_large_image' : 'summary';
    const titleForHead = resolvedTitle ? `${resolvedTitle}${NO_APP_SUFFIX_MARKER}` : '';

    return (
        <Head title={titleForHead}>
            {resolvedDescription && (
                <meta
                    name="description"
                    content={resolvedDescription}
                    head-key="description"
                />
            )}
            {canonicalUrl && (
                <link rel="canonical" href={canonicalUrl} head-key="canonical" />
            )}
            <meta property="og:type" content={ogType} head-key="og:type" />
            {resolvedTitle && (
                <meta
                    property="og:title"
                    content={resolvedTitle}
                    head-key="og:title"
                />
            )}
            {resolvedDescription && (
                <meta
                    property="og:description"
                    content={resolvedDescription}
                    head-key="og:description"
                />
            )}
            {canonicalUrl && (
                <meta property="og:url" content={canonicalUrl} head-key="og:url" />
            )}
            {resolvedImage && (
                <meta
                    property="og:image"
                    content={resolvedImage}
                    head-key="og:image"
                />
            )}
            {companyName && (
                <meta
                    property="og:site_name"
                    content={companyName}
                    head-key="og:site_name"
                />
            )}
            <meta
                name="twitter:card"
                content={twitterCard}
                head-key="twitter:card"
            />
            {resolvedTitle && (
                <meta
                    name="twitter:title"
                    content={resolvedTitle}
                    head-key="twitter:title"
                />
            )}
            {resolvedDescription && (
                <meta
                    name="twitter:description"
                    content={resolvedDescription}
                    head-key="twitter:description"
                />
            )}
            {resolvedImage && (
                <meta
                    name="twitter:image"
                    content={resolvedImage}
                    head-key="twitter:image"
                />
            )}
            {children}
        </Head>
    );
}
